/**
 * Custom hook for visitor data management
 * Handles API calls, caching, and offline functionality
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import visitorLocalStorage from '../../../utils/visitorLocalStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useVisitorData = () => {
  const [visitors, setVisitors] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      syncPendingActions();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // API call helper with offline fallback
  const apiCall = useCallback(async (url, options = {}) => {
    try {
      const response = await axios({
        url,
        baseURL: BACKEND_URL,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      return response.data;
    } catch (error) {
      if (isOffline) {
        console.log('Offline mode: falling back to localStorage');
        throw new Error('OFFLINE');
      }
      throw error;
    }
  }, [isOffline]);

  // Helper function to upload visitor photo
  const uploadVisitorPhoto = useCallback(async (visitorId, photoData, description = null) => {
    console.log('🔄 Starting photo upload for visitor:', visitorId);
    console.log('📸 Photo data type:', typeof photoData);
    console.log('📸 Photo data length:', photoData?.length);
    console.log('📝 Description:', description);
    
    try {
      // Convert base64 to blob
      console.log('🔄 Converting base64 to blob...');
      const response = await fetch(photoData);
      const blob = await response.blob();
      console.log('✅ Blob created:', { size: blob.size, type: blob.type });
      
      // Create file from blob
      const fileName = `visitor_photo_${Date.now()}.jpg`;
      const file = new File([blob], fileName, {
        type: 'image/jpeg'
      });
      console.log('✅ File created:', { name: file.name, size: file.size, type: file.type });
      
      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }
      console.log('✅ FormData prepared with file and description');
      
      // Upload photo
      const uploadUrl = `/api/visitors/${visitorId}/photos`;
      console.log('🚀 Uploading to:', uploadUrl);
      
      const photoResponse = await axios({
        url: uploadUrl,
        baseURL: BACKEND_URL,
        method: 'POST',
        data: formData,
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ Photo upload successful:', photoResponse.data);
      console.log('📊 Response status:', photoResponse.status);
      return photoResponse.data;
    } catch (error) {
      console.error('❌ Error uploading visitor photo:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  }, []);

  // Fetch all visitors
  const fetchVisitors = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        const offlineVisitors = await visitorLocalStorage.getVisitors(filters);
        setVisitors(offlineVisitors);
      } else {
        const queryParams = new URLSearchParams();
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.building_id) queryParams.append('building_id', filters.building_id);
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.limit) queryParams.append('limit', filters.limit);
        if (filters.offset) queryParams.append('offset', filters.offset);

        const url = `/api/visitors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const data = await apiCall(url);
        setVisitors(data.visitors || data);

        // Cache in localStorage for offline use
        if (data.visitors) {
          for (const visitor of data.visitors) {
            await visitorLocalStorage.saveVisitor(visitor);
          }
        }
      }
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const offlineVisitors = await visitorLocalStorage.getVisitors(filters);
        setVisitors(offlineVisitors);
      } else {
        setError(error.message);
        console.error('Error fetching visitors:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Fetch active visitors
  const fetchActiveVisitors = useCallback(async (buildingId = null) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        const offlineActive = await visitorLocalStorage.getActiveVisitors(buildingId);
        setActiveVisitors(offlineActive);
      } else {
        const url = buildingId 
          ? `/api/visitors/active?building_id=${buildingId}`
          : '/api/visitors/active';
        const data = await apiCall(url);
        setActiveVisitors(data.visitors || []);

        // Cache in localStorage
        if (data.visitors) {
          for (const visitor of data.visitors) {
            await visitorLocalStorage.saveVisitor(visitor);
          }
        }
      }
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const offlineActive = await visitorLocalStorage.getActiveVisitors(buildingId);
        setActiveVisitors(offlineActive);
      } else {
        setError(error.message);
        console.error('Error fetching active visitors:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Register new visitor
  const registerVisitor = useCallback(async (visitorData) => {
    setLoading(true);
    setError(null);

    try {
      // Filter data to only include fields expected by backend
      const backendData = {
        first_name: visitorData.first_name,
        last_name: visitorData.last_name,
        email: visitorData.email,
        phone: visitorData.phone,
        company: visitorData.company,
        purpose: visitorData.purpose,
        host_id: visitorData.host_id,
        host_email: visitorData.host_email,
        property_id: visitorData.property_id,
        expected_duration: visitorData.expected_duration,
        expected_checkin: visitorData.expected_checkin,
        expected_checkout: visitorData.expected_checkout
      };

      if (isOffline) {
        // Save to localStorage with offline flag
        const offlineVisitor = {
          ...visitorData,
          id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          status: 'pending',
          created_at: new Date().toISOString(),
          is_offline: true
        };
        
        await visitorLocalStorage.saveVisitor(offlineVisitor);
        
        // Save as pending action for sync (use filtered data)
        await visitorLocalStorage.savePendingAction({
          action_type: 'REGISTER_VISITOR',
          data: backendData,
          created_at: new Date().toISOString()
        });

        // Update visitors state to include the new visitor
        setVisitors(prev => [offlineVisitor, ...prev]);

        return offlineVisitor;
      } else {
        const data = await apiCall('/api/visitors/register', {
          method: 'POST',
          data: backendData,
        });

        // Upload photo if provided
        console.log('🔍 Checking for photo data in registration:', !!visitorData.photo_data);
        let uploadedPhoto = null;
        if (visitorData.photo_data) {
          console.log('📸 Photo data found, starting upload process...');
          try {
            uploadedPhoto = await uploadVisitorPhoto(
              data.id, 
              visitorData.photo_data, 
              'Registration photo'
            );
            console.log('✅ Photo uploaded successfully during registration:', uploadedPhoto);
          } catch (photoError) {
            console.error('❌ Failed to upload photo during registration:', photoError);
            // Don't fail the entire registration if photo upload fails
          }
        } else {
          console.log('ℹ️ No photo data provided during registration');
        }

        // Fetch updated visitor data with photos
        console.log('🔄 Fetching updated visitor data...');
        let finalVisitorData = data;
        if (uploadedPhoto) {
          console.log('📸 Photo was uploaded, fetching updated visitor data with photos...');
          try {
            // Fetch the visitor again to get the updated photos
            const updatedVisitor = await apiCall(`/api/visitors/${data.id}`);
            console.log('✅ Updated visitor data retrieved:', {
              id: updatedVisitor.id,
              hasPhotos: !!updatedVisitor.photos,
              photoCount: updatedVisitor.photos?.length || 0
            });
            finalVisitorData = updatedVisitor;
          } catch (fetchError) {
            console.error('❌ Failed to fetch updated visitor data:', fetchError);
            // Use original data if fetch fails
          }
        } else {
          console.log('ℹ️ No photo uploaded, using original visitor data');
        }

        // Cache in localStorage with full visitor data
        const fullVisitorData = {
          ...finalVisitorData,
          photo_data: visitorData.photo_data,
          documents: visitorData.documents
        };
        await visitorLocalStorage.saveVisitor(fullVisitorData);
        
        // Update visitors state to include the new visitor
        setVisitors(prev => [fullVisitorData, ...prev]);
        
        return finalVisitorData;
      }
    } catch (error) {
      if (error.message === 'OFFLINE') {
        // Handle offline registration
        const offlineVisitor = {
          ...visitorData,
          id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          status: 'pending',
          created_at: new Date().toISOString(),
          is_offline: true
        };
        
        await visitorLocalStorage.saveVisitor(offlineVisitor);
        
        // Filter data for pending action
        const backendData = {
          first_name: visitorData.first_name,
          last_name: visitorData.last_name,
          email: visitorData.email,
          phone: visitorData.phone,
          company: visitorData.company,
          purpose: visitorData.purpose,
          host_id: visitorData.host_id,
          host_email: visitorData.host_email,
          property_id: visitorData.property_id,
          expected_duration: visitorData.expected_duration,
          expected_checkin: visitorData.expected_checkin,
          expected_checkout: visitorData.expected_checkout
        };
        
        await visitorLocalStorage.savePendingAction({
          action_type: 'REGISTER_VISITOR',
          data: backendData
        });

        // Update visitors state to include the new visitor
        setVisitors(prev => [offlineVisitor, ...prev]);

        return offlineVisitor;
      } else {
        setError(error.message);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Check in visitor
  const checkInVisitor = useCallback(async (visitorId) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        // Update local visitor status
        const visitor = await visitorLocalStorage.getVisitor(visitorId);
        if (visitor) {
          const updatedVisitor = {
            ...visitor,
            status: 'checked_in',
            checked_in_at: new Date().toISOString()
          };
          await visitorLocalStorage.saveVisitor(updatedVisitor);
          
          // Save as pending action
          await visitorLocalStorage.savePendingAction({
            action_type: 'CHECKIN_VISITOR',
            visitor_id: visitorId,
            data: { checked_in_at: new Date().toISOString() }
          });

          // Update visitors state
          setVisitors(prev => prev.map(v => v.id === visitorId ? updatedVisitor : v));

          return updatedVisitor;
        }
      } else {
        const data = await apiCall(`/api/visitors/check-in`, {
          method: 'POST',
          data: { visitor_id: visitorId }
        });

        // Update cache
        await visitorLocalStorage.saveVisitor(data);
        
        // Update visitors state
        setVisitors(prev => prev.map(v => v.id === visitorId ? data : v));
        
        return data;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Check out visitor
  const checkOutVisitor = useCallback(async (visitorId) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        // Update local visitor status
        const visitor = await visitorLocalStorage.getVisitor(visitorId);
        if (visitor) {
          const updatedVisitor = {
            ...visitor,
            status: 'checked_out',
            checked_out_at: new Date().toISOString()
          };
          await visitorLocalStorage.saveVisitor(updatedVisitor);
          
          // Save as pending action
          await visitorLocalStorage.savePendingAction({
            action_type: 'CHECKOUT_VISITOR',
            visitor_id: visitorId,
            data: { checked_out_at: new Date().toISOString() }
          });

          // Update visitors state
          setVisitors(prev => prev.map(v => v.id === visitorId ? updatedVisitor : v));

          return updatedVisitor;
        }
      } else {
        const data = await apiCall(`/api/visitors/check-out`, {
          method: 'POST',
          data: { visitor_id: visitorId }
        });

        // Update cache
        await visitorLocalStorage.saveVisitor(data);
        
        // Update visitors state
        setVisitors(prev => prev.map(v => v.id === visitorId ? data : v));
        
        return data;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Approve visitor
  const approveVisitor = useCallback(async (visitorId) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        const visitor = await visitorLocalStorage.getVisitor(visitorId);
        if (visitor) {
          const updatedVisitor = {
            ...visitor,
            status: 'approved',
            approved_at: new Date().toISOString()
          };
          await visitorLocalStorage.saveVisitor(updatedVisitor);
          
          await visitorLocalStorage.savePendingAction({
            action_type: 'APPROVE_VISITOR',
            visitor_id: visitorId,
            data: { approved_at: new Date().toISOString() }
          });

          // Update visitors state
          setVisitors(prev => prev.map(v => v.id === visitorId ? updatedVisitor : v));

          return updatedVisitor;
        }
      } else {
        const data = await apiCall(`/api/visitors/approve`, {
          method: 'POST',
          data: { visitor_id: visitorId, approved: true }
        });

        await visitorLocalStorage.saveVisitor(data);
        
        // Update visitors state
        setVisitors(prev => prev.map(v => v.id === visitorId ? data : v));
        
        return data;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  // Sync pending actions when back online
  const syncPendingActions = useCallback(async () => {
    if (isOffline) return;

    try {
      const pendingActions = await visitorLocalStorage.getPendingActions();
      
      for (const action of pendingActions) {
        try {
          let endpoint = '';
          let method = 'POST';
          let data = null;

          switch (action.action_type) {
            case 'REGISTER_VISITOR':
              endpoint = '/api/visitors/register';
              data = action.data;
              break;
            case 'CHECKIN_VISITOR':
              endpoint = `/api/visitors/check-in`;
              data = { visitor_id: action.visitor_id };
              break;
            case 'CHECKOUT_VISITOR':
              endpoint = `/api/visitors/check-out`;
              data = { visitor_id: action.visitor_id };
              break;
            case 'APPROVE_VISITOR':
              endpoint = `/api/visitors/approve`;
              data = { visitor_id: action.visitor_id, approved: true };
              break;
            default:
              continue;
          }

          await apiCall(endpoint, { method, data });
          await visitorLocalStorage.deletePendingAction(action.id);
        } catch (error) {
          console.error(`Failed to sync action ${action.id}:`, error);
          // Increment retry count or handle max retries
        }
      }
    } catch (error) {
      console.error('Error syncing pending actions:', error);
    }
  }, [apiCall, isOffline]);

  // Get visitor by ID with full details
  const getVisitorById = useCallback(async (visitorId) => {
    setLoading(true);
    setError(null);

    try {
      if (isOffline) {
        // Try to get from localStorage first
        const cachedVisitor = await visitorLocalStorage.getVisitor(visitorId);
        if (cachedVisitor) {
          return cachedVisitor;
        }
        throw new Error('Visitor not found in offline cache');
      } else {
        const apiUrl = `/api/visitors/${visitorId}`;
        const data = await apiCall(apiUrl);
        
        // Cache the detailed visitor data
        await visitorLocalStorage.saveVisitor(data);
        
        return data;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, isOffline]);

  return {
    visitors,
    activeVisitors,
    loading,
    error,
    isOffline,
    fetchVisitors,
    fetchActiveVisitors,
    registerVisitor,
    checkInVisitor,
    checkOutVisitor,
    approveVisitor,
    getVisitorById,
    syncPendingActions,
    uploadVisitorPhoto,
    setError
  };
};

export default useVisitorData;